/**
 * Dev-only: in `vite dev` (`import.meta.env.DEV`), inject the X-Dev-Bypass header so the local
 * browser authenticates as the dev admin without OAuth — making the full UI verifiable on
 * localhost. Double-gated: the server only honours this header when BETTER_AUTH_URL is localhost
 * (middleware/auth.ts), and `import.meta.env.DEV` is false in any production build, so it's dead
 * code in prod. There is no role-simulation query parameter: the server has no way to resolve a
 * requested role without a real D1 user_roles row for the dev-bypass identity (#2930), so this
 * only ever authenticates as the dev admin.
 */
import { endpointPath, logUsageEvent, resultCount } from "./telemetry-events";
import { makeApiError } from "./api-error";
import { canReportProgress, UPLOAD_CANCELLED_STATUS, xhrUpload, type ProgressHandler } from "./upload-xhr";

/** #1876 — usage beacon for API traffic. Never times telemetry's own posts. */
function recordApiEvent(
  kind: "api_read" | "api_write" | "api_error",
  path: string,
  startedAt: number,
  count: number | null = null,
): void {
  try {
    if (path.includes("/api/telemetry")) return;
    logUsageEvent({
      eventType: kind,
      endpoint: endpointPath(path),
      durationMs: Math.round(performance.now() - startedAt),
      resultCount: count,
    });
  } catch {
    // Telemetry must never interrupt the application.
  }
}

/** Exported for the #2712 error reporter, which posts with its own keepalive fetch. */
export function devAuthHeaders(): Record<string, string> {
  if (!import.meta.env.DEV) return {};
  return { "X-Dev-Bypass": "true" };
}

/**
 * Typed fetch wrapper for API calls.
 * Throws on non-OK responses with the error body.
 */
export async function apiFetch<T>(
  path: string,
  params?: Record<string, string | number | boolean | undefined>,
  /** #4922: a query's AbortSignal, so a cancelled query aborts its request. Absent, the request is sent exactly as before. */
  signal?: AbortSignal,
): Promise<T> {
  const url = new URL(path, window.location.origin);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const startedAt = performance.now();
  const res = await fetch(url.toString(), {
    credentials: "include",
    headers: { ...devAuthHeaders() },
    ...(signal ? { signal } : {}),
  });

  if (!res.ok) {
    const body = await res.text();
    recordApiEvent("api_error", path, startedAt);
    throw makeApiError(res.status, body);
  }

  const data = (await res.json()) as T;
  recordApiEvent("api_read", path, startedAt, resultCount(data));
  return data;
}

/**
 * Credentialed fetch returning raw text — for endpoints that serve HTML
 * rather than JSON (e.g. /api/artifacts/:id/render, injected into a
 * sandboxed iframe via srcDoc so the document itself never needs cookies).
 */
export async function apiFetchText(path: string): Promise<string> {
  const res = await fetch(path, {
    credentials: "include",
    headers: { ...devAuthHeaders() },
  });
  if (!res.ok) throw makeApiError(res.status, await res.text());
  return res.text();
}

/**
 * Fetch a file the server sends as an attachment (CSV/XLSX export) and hand it
 * back as a Blob with the filename from Content-Disposition, so the caller can
 * trigger the browser download. Same credentials + dev headers as apiFetch.
 */
export async function apiFetchBlob(path: string): Promise<{ blob: Blob; filename: string | null }> {
  const res = await fetch(path, {
    credentials: "include",
    headers: { ...devAuthHeaders() },
  });
  if (!res.ok) throw makeApiError(res.status, await res.text());
  const disposition = res.headers.get("content-disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  return { blob: await res.blob(), filename: match ? match[1] : null };
}

/** Error thrown by the API wrappers, carrying the HTTP status + raw body so
 *  callers (e.g. QueryErrorBanner) can distinguish 403 access-boundaries from
 *  5xx/network faults without string-parsing the message. Built in
 *  lib/api-error.ts so the XHR upload path throws the same shape. */
export type { ApiError } from "./api-error";

/**
 * Typed fetch wrapper for write operations (POST/PUT/PATCH/DELETE).
 * Throws on non-OK responses with the error body.
 */
export async function apiMutate<T>(
  path: string,
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  body?: object,
  /** #3884: lets a caller drop a stalled write. Absent, the request is sent exactly as before. */
  signal?: AbortSignal,
): Promise<T> {
  const startedAt = performance.now();
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...devAuthHeaders() },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
  });

  if (!res.ok) {
    const text = await res.text();
    recordApiEvent("api_error", path, startedAt);
    throw makeApiError(res.status, text);
  }

  recordApiEvent("api_write", path, startedAt);
  return res.json();
}

/**
 * Multipart upload (#2059 Cases attachments; the evidence hook hand-rolled
 * the same fetch). No Content-Type header: the browser sets the boundary.
 *
 * With an `onProgress` handler the request goes through XMLHttpRequest, the
 * only transport a browser reports bytes-sent on (#2261). A `signal` sends it
 * that way too (#2424): fetch would honour the cancel but carries no stall
 * watchdog, so a caller asking to be able to stop an upload gets the window
 * that stops a dead one as well. Neither: it stays on fetch.
 */
export async function apiUpload<T>(path: string, form: FormData, onProgress?: ProgressHandler, signal?: AbortSignal): Promise<T> {
  const startedAt = performance.now();
  if ((onProgress || signal) && canReportProgress()) {
    return trackedUpload<T>(path, startedAt, { method: "POST", url: path, body: form, headers: devAuthHeaders(), onProgress, signal });
  }
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { ...devAuthHeaders() },
    body: form,
    signal,
  }).catch(cancelledAs(signal));
  if (!res.ok) {
    const text = await res.text();
    recordApiEvent("api_error", path, startedAt);
    throw makeApiError(res.status, text);
  }
  recordApiEvent("api_write", path, startedAt);
  return res.json();
}

/**
 * Raw-body upload (#2059): the File IS the body, streamed by the browser with
 * its own Content-Length and type, so a 60 MB video never passes through
 * FormData. `params` become the query string the server reads the metadata from.
 *
 * Same transport rule as apiUpload: progress or a signal means XHR, so the
 * poster that follows a raw upload gets the watchdog on its signal alone.
 */
export async function apiUploadRaw<T>(
  path: string,
  file: File,
  params: Record<string, string | undefined> = {},
  onProgress?: ProgressHandler,
  signal?: AbortSignal,
): Promise<T> {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") url.searchParams.set(key, value);
  }
  const startedAt = performance.now();
  const contentType = file.type || "application/octet-stream";
  if ((onProgress || signal) && canReportProgress()) {
    return trackedUpload<T>(path, startedAt, {
      method: "PUT",
      url: url.toString(),
      body: file,
      headers: { "Content-Type": contentType, ...devAuthHeaders() },
      onProgress,
      signal,
    });
  }
  const res = await fetch(url.toString(), {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": contentType, ...devAuthHeaders() },
    body: file,
    signal,
  }).catch(cancelledAs(signal));
  if (!res.ok) {
    const text = await res.text();
    recordApiEvent("api_error", path, startedAt);
    throw makeApiError(res.status, text);
  }
  recordApiEvent("api_write", path, startedAt);
  return res.json();
}

/**
 * #2399: fetch reports an abort as a DOMException with no status, which the
 * upload queue would read as a dropped connection and send again. Only the
 * XHR arm normally runs for a visit upload (it is the one with progress), but
 * a runtime without XMLHttpRequest must not turn a cancel into a retry.
 */
function cancelledAs(signal: AbortSignal | undefined): (err: unknown) => never {
  return (err: unknown) => {
    if (signal?.aborted) throw makeApiError(UPLOAD_CANCELLED_STATUS, "cancelled");
    throw err;
  };
}

/**
 * The XHR arm of both upload wrappers: same telemetry, same thrown shape,
 * plus the bytes-sent callback fetch cannot give.
 */
async function trackedUpload<T>(
  path: string,
  startedAt: number,
  opts: Parameters<typeof xhrUpload>[0],
): Promise<T> {
  try {
    const data = await xhrUpload<T>(opts);
    recordApiEvent("api_write", path, startedAt);
    return data;
  } catch (err) {
    recordApiEvent("api_error", path, startedAt);
    throw err;
  }
}
